/* eslint-disable @typescript-eslint/naming-convention */
import React, { Component } from 'react'
import mem from 'mem'
import { addHours, hourDifference } from 'legacy/common/dateUtils'
import { type ShiftType, type Shift, type TransformedShift } from 'domains/shifts/components/types'
import ShiftRow from 'domains/shifts/components/ShiftRow'
import { ShiftManagementContext } from 'domains/shifts/components/contexts/ShiftManagementContext'

const transformShiftDates = mem(
  // mem will break in ie11 if we specify the args...
  (...args: [Shift[]]): TransformedShift[] =>
    args[0].map(shift => ({
      ...shift,
      starts_at: new Date(shift.starts_at),
      ends_at: new Date(shift.ends_at),
    }))
)

function shouldMergeLeft(
  prevShift: TransformedShift,
  shiftInfo: { shift_type: string; recurring_shift_id?: number },
  start: Date
) {
  return (
    prevShift.ends_at.getTime() >= start.getTime() &&
    start.getTime() >= prevShift.starts_at.getTime() &&
    prevShift.shift_type === shiftInfo.shift_type &&
    !prevShift.recurring_shift_id &&
    !shiftInfo.recurring_shift_id
  )
}

function shouldMergeRight(
  nextShift: TransformedShift,
  shiftInfo: { shift_type: string; recurring_shift_id?: number },
  end: Date
) {
  return (
    nextShift.starts_at.getTime() <= end.getTime() &&
    end.getTime() < nextShift.ends_at.getTime() &&
    nextShift.shift_type === shiftInfo.shift_type &&
    !nextShift.recurring_shift_id &&
    !shiftInfo.recurring_shift_id
  )
}

interface Props {
  warehouseLocationId: string
  activeShiftType: ShiftType
  first: boolean
  even: boolean
  shifts: Shift[]
  onAddShift(start: Date, end: Date, onCall: boolean, modalStyles?: Object): void
  onChangeShift(id: number, start: Date, end: Date, onCall: boolean): Promise<void>
  onSetShiftRecurrence?(shiftId: number, isRecurring: boolean): void
  zoneOffset: number
  devicePrioritizationEnabled: boolean
  shopperId: number
  recurShiftsToBeNonRecur: number[]
  rowRefs: React.RefObject<HTMLDivElement>[]
  selectedRecommendationShopperId?: number
  ultrafastCrossDayDeliveryEnabled?: boolean
}

class ShiftsRowContainer extends Component<Props> {
  static contextType = ShiftManagementContext

  get shifts() {
    return transformShiftDates(this.props.shifts)
  }

  addShift(start: Date, end: Date, onCall: boolean, modalStyles?: Object) {
    const { activeShiftType, onAddShift } = this.props
    const { regularStoreOpen: maxStart, regularStoreClose: maxEnd } = this.context
    const { shifts, handleChangeShift } = this

    if (start < maxStart || end > maxEnd) {
      return false
    }

    for (let i = 0; i < shifts.length; i += 1) {
      const shift = shifts[i]

      if (shift.id && shouldMergeLeft(shift, { shift_type: activeShiftType }, start)) {
        handleChangeShift(shift.id, shift.starts_at, end, onCall)

        return true
      }

      if (shift.id && shouldMergeRight(shift, { shift_type: activeShiftType }, end)) {
        handleChangeShift(shift.id, start, shift.ends_at, onCall)

        return true
      }

      if (shift.starts_at > start) {
        break
      }
    }

    if (hourDifference(start, end) >= 1) {
      // allow for 1-hour schedule length
      onAddShift(start, end, onCall, modalStyles)

      return true
    }

    return false
  }

  handleAddShift = (start: Date, end: Date, onCall: boolean, modalStyles?: Object) => {
    if (this.addShift(start, end, onCall, modalStyles)) {
      return
    }

    if (this.addShift(start, addHours(end, 1), onCall, modalStyles)) {
      return
    }

    this.addShift(addHours(start, -1), end, onCall, modalStyles)
  }
  handleChangeShift = async (id: number, start: Date, end: Date, onCall: boolean) => {
    const { onChangeShift } = this.props
    const { shifts } = this
    const shiftIndex = shifts.findIndex(shift => shift.id === id)
    const shift = shifts[shiftIndex]

    const prevShift = shifts[shiftIndex - 1]

    if (prevShift && prevShift.id && shouldMergeLeft(prevShift, shift, start)) {
      return onChangeShift(prevShift.id, prevShift.starts_at, end, onCall)
    }

    const nextShift = shifts[shiftIndex + 1]

    if (nextShift && nextShift.id && shouldMergeRight(nextShift, shift, end)) {
      return onChangeShift(nextShift.id, start, nextShift.ends_at, onCall)
    }

    return onChangeShift(id, start, end, onCall)
  }

  render() {
    const {
      first,
      even,
      activeShiftType,
      zoneOffset,
      recurShiftsToBeNonRecur,
      rowRefs,
      shopperId,
      devicePrioritizationEnabled,
      warehouseLocationId,
      ultrafastCrossDayDeliveryEnabled,
    } = this.props
    const passProps = {
      first,
      even,
      rowRefs,
      recurShiftsToBeNonRecur,
      devicePrioritizationEnabled,
      shopperId,
      zoneOffset,
      activeShiftType,
      warehouseLocationId,
      ultrafastCrossDayDeliveryEnabled,
    }
    const { shifts } = this

    return <ShiftRow shifts={shifts} onAddShift={this.handleAddShift} {...passProps} />
  }
}

export default ShiftsRowContainer
